import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import HomeLayout from "@app/layouts/Home/HomeLayout";
import { TeaserData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import {
  GA4_REF_CODE_ANCHORBLOCK_A,
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_LATEST_BLOCKS,
} from "@app/utils/ga/ref-code";
import {
  buildUrlWithGARefCode,
  getContentBlockRefCode,
} from "@app/utils/ga/tracking";
import { createTeaserMapper } from "@app/utils/mapTeaserDataToProps";
import MetaData from "@components/Modules/MetaData";
import PubBaseLayout from "@pub/BaseLayout";
import { configGTM, configHome, configSite } from "@pub/config";
import {
  GlobalBaseData,
  ImageCrop,
  useBaseContext,
} from "@sphtech/dmg-design-system";
import {
  ResponseType,
  TRedirectResponse,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { useLoaderData } from "react-router";

import { fetchHomeData } from "./Home.server";
import { HomeElementConfigData } from "./utils";

export type HomeData = {
  listBlocks: {
    type: "list" | "section" | "tag";
    section: string;
    key: string;
    title: string;
    titleZH?: string;
    url?: string;
    teasers?: TeaserData[];
    code: string;
    cta?: boolean;
    config?: HomeElementConfigData;
    backgroundImage?: ImageCrop;
  }[];
  topStories: TeaserData[];
  socialVideo?: {
    type: "list";
    key: string;
    title: string;
    titleZH: string;
    url?: string;
    teasers?: { embedUri?: string }[];
  };
  blocks: {
    type: "list" | "section" | "tag";
    section: string;
    key: string;
    title: string;
    titleZH?: string;
    url?: string;
    teasers?: TeaserData[];
    code: string;
    cta?: boolean;
    config?: HomeElementConfigData;
    backgroundImage?: ImageCrop;
  }[];
};

export function meta() {
  const pageTitle = configSite.tagline;
  return [
    ...MetaData({
      title: pageTitle,
      path: "",
      type: "WebPage",
      description: configSite.description,
    }),
    { name: "google-site-verification", content: configGTM.siteVerification },
  ];
}

export default function HomePage() {
  const response: TRouteWithRedirect<HomeData, string> = useLoaderData();
  const globalData = useBaseContext();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const magIssues = globalData?.magazineIssues;
  const magIssuesConfig = magIssues?.config;
  const userSubmissionBox = globalData?.userSubmissionBox;

  const spotlightStory = response.payload.blocks.find(
    (block) => block.key === "spotlight_story",
  );

  const topStory = response.payload.topStories[0];
  // Filter out based on displayPublishDate configuration
  const teaserOverwrites = configHome.teaser?.displayPublishDate
    ? undefined
    : { publishDate: undefined, updateDate: undefined };

  return (
    <PubBaseLayout>
      <AdSettings
        pageType="homepage"
        sectionName="Homepage"
        adUnits="lb1,lb2,imu1,imu2,imu3,catfish,prestitial"
        pageTitle=""
        currentLocation={{
          pathname: "/",
        }}
      />

      <HomeLayout
        data={{
          AnchorBlockProps: topStory
            ? createTeaserMapper({
                gaRefCode: GA4_REF_CODE_ANCHORBLOCK_A,
                overwrites: teaserOverwrites,
              })(topStory)
            : undefined,
          latestArticle: {
            items: response.payload.topStories.slice(1).map((teaser) => {
              return createTeaserMapper({
                gaRefCode:
                  configHome.latestArticle.refCode ||
                  GA4_REF_CODE_LATEST_BLOCKS,
                overwrites: teaserOverwrites,
              })(teaser);
            }),
            viewMore: {
              url: buildUrlWithGARefCode(
                "/latest-stories",
                configHome.latestArticle.refCode || GA4_REF_CODE_LATEST_BLOCKS,
              ),
              text: "VIEW ALL",
            },
          },
          magazineIssues: {
            type: "list",
            title: magIssues?.title || "",
            titleZH: magIssues?.titleZH,
            url: magIssues?.url || "",
            subscriptionInfo: {
              heading: magIssuesConfig?.home?.title || "",
              description: magIssuesConfig?.home?.subtitle || "",
              buttonText: magIssuesConfig?.home?.buttonText || "",
              buttonUrl: magIssuesConfig?.home?.buttonUrl || "",
              hideSubscriptionInfo:
                magIssuesConfig?.home?.hideSubscriptionInfo || "",
            },
            items:
              magIssues?.teasers?.map((issue) => {
                const crop =
                  issue.image?.crops.original || issue.image?.crops.portrait;
                return {
                  url: issue.path || "",
                  image: crop?.src || "",
                  srcWidth: crop?.srcWidth || 0,
                  srcHeight: crop?.srcHeight || 0,
                  alt: issue.image?.alt || "",
                  caption: issue.title || "",
                  description: issue.description || "",
                };
              }) || [],
          },
          userSubmissionBox,
          socialVideo: response.payload.socialVideo && {
            type: "list",
            title: response.payload.socialVideo.title || "",
            titleZH: response.payload.socialVideo.titleZH || "",
            url: response.payload.socialVideo.url || "",
            items:
              response.payload.socialVideo.teasers?.map((teaser) => ({
                embedUrl: teaser.embedUri || "",
              })) || [],
          },
          contentBlock: response.payload.blocks.map((block) => ({
            type: block.type,
            title: block.title,
            titleZH: block.titleZH,
            uniqSectionName: block.section.startsWith("section-")
              ? block.section.replace("section-", "")
              : undefined,
            key: block.key,
            url: buildUrlWithGARefCode(
              block.url || "",
              getContentBlockRefCode(block.code),
            ),
            cta: block.cta !== false,
            items: block.teasers
              ? block.teasers.map(
                  createTeaserMapper({
                    gaRefCode: getContentBlockRefCode(block.code),
                    overwrites: teaserOverwrites,
                  }),
                )
              : [],
            withImu: false,
            config: block.config,
            backgroundImage: block.backgroundImage,
          })),
          spotlightStoryProps: spotlightStory?.teasers?.[0]
            ? createTeaserMapper({
                gaRefCode: GA4_REF_CODE_ANCHORBLOCK_B,
                overwrites: teaserOverwrites,
              })(spotlightStory.teasers[0])
            : undefined,
        }}
      />
    </PubBaseLayout>
  );
}

export async function loader(): Promise<
  | TRouteWithoutRedirect<HomeData | GlobalBaseData, GlobalBaseData | string>
  | TRedirectResponse
> {
  const response = await fetchHomeData();

  return await dataLoader({
    type: response.type,
    dataSource: response,
  });
}
